import React from 'react'
import { Star, Quote, Camera, TrendingUp, Clock } from 'lucide-react'
import { useTestVariant } from '../../contexts/ABTestContext'

interface Testimonial {
  id: string
  name: string
  role: string
  company: string
  image: string
  content: string
  rating: number
  metrics?: {
    timeSaved?: string
    revenueIncrease?: string
    efficiency?: string
  }
  featured?: boolean
}

interface TestimonialsSectionProps {
  className?: string
}

export const TestimonialsSection: React.FC<TestimonialsSectionProps> = ({ className = '' }) => {
  const testimonialsVariant = useTestVariant('testimonials_count')
  
  const testimonials: Testimonial[] = [
    {
      id: '1',
      name: 'Sarah Chen',
      role: 'Stock Photographer',
      company: 'Getty Images Contributor',
      image: '/api/placeholder/64/64',
      content: "This is a game-changer! I used to spend 6-8 hours daily just writing metadata. Now I process 1000+ photos in under an hour. My stock sales increased 340% in 3 months because my photos are finally discoverable.",
      rating: 5,
      metrics: {
        timeSaved: '7 hours/day',
        revenueIncrease: '+340%'
      },
      featured: true
    },
    {
      id: '2',
      name: 'Marcus Rodriguez',
      role: 'Wedding Photographer',
      company: 'Rodriguez Photography Studio',
      image: '/api/placeholder/64/64',
      content: "I was skeptical about AI writing my metadata, but the quality is incredible. It understands context better than I do sometimes. My SEO rankings improved dramatically and client discovery increased by 85%.",
      rating: 5,
      metrics: {
        efficiency: '20x faster',
        revenueIncrease: '+85%'
      }
    },
    {
      id: '3',
      name: 'Emma Thompson',
      role: 'Agency Creative Director',
      company: 'Visual Impact Agency',
      image: '/api/placeholder/64/64',
      content: "We manage metadata for 50+ photographers. This tool saved us from hiring 3 additional staff members. The consistency and quality are outstanding. ROI was 1200% in the first quarter.",
      rating: 5,
      metrics: {
        timeSaved: '120 hours/week',
        efficiency: '1200% ROI'
      }
    },
    {
      id: '4',
      name: 'David Kim',
      role: 'Nature Photographer',
      company: 'National Geographic Contributor',
      image: '/api/placeholder/64/64',
      content: "The AI understands wildlife and nature contexts amazingly well. Keywords I never would have thought of are generating sales. My stock earnings doubled in 6 months.",
      rating: 5,
      metrics: {
        revenueIncrease: '+200%',
        efficiency: '15x faster'
      }
    },
    {
      id: '5',
      name: 'Lisa Martinez',
      role: 'Product Photographer',
      company: 'E-commerce Solutions Inc.',
      image: '/api/placeholder/64/64',
      content: "Perfect for e-commerce! It generates product-specific metadata that actually converts. Our client retention improved because their products rank higher in searches.",
      rating: 5,
      metrics: {
        efficiency: '25x faster',
        timeSaved: '30 hours/week'
      }
    },
    {
      id: '6',
      name: 'Alex Johnson',
      role: 'Travel Photographer',
      company: 'Wanderlust Visuals',
      image: '/api/placeholder/64/64',
      content: "Traveling constantly meant metadata was always my bottleneck. Now I upload from anywhere and have professional metadata ready instantly. Game-changing for my workflow.",
      rating: 5,
      metrics: {
        timeSaved: '5 hours/day',
        efficiency: '10x faster'
      }
    }
  ]

  const stats = [
    {
      icon: <Camera className="w-8 h-8" />,
      value: '10M+',
      label: 'Photos Processed',
      description: 'Successfully tagged with AI'
    },
    {
      icon: <TrendingUp className="w-8 h-8" />,
      value: '340%',
      label: 'Avg Revenue Increase',
      description: 'For our active users'
    },
    {
      icon: <Clock className="w-8 h-8" />,
      value: '25x',
      label: 'Faster Processing',
      description: 'Compared to manual work'
    },
    {
      icon: <Star className="w-8 h-8" />,
      value: '99.2%',
      label: 'Satisfaction Rate',
      description: 'From 10,000+ users'
    }
  ]

  const renderStars = (rating: number) => {
    return Array.from({ length: 5 }, (_, i) => (
      <Star
        key={i}
        className={`w-5 h-5 ${
          i < rating ? 'text-yellow-400 fill-current' : 'text-gray-300'
        }`}
      />
    ))
  }

  return (
    <section className={`py-20 bg-white ${className}`} id="testimonials">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16">
          <h2 className="text-4xl sm:text-5xl font-bold text-gray-900 mb-6">
            Join 10,000+ Photographers 
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-purple-600"> Getting Results</span>
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            See how photographers are saving hours daily and increasing their revenue with AI-powered metadata generation.
          </p>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-6 sm:gap-8 mb-16">
          {stats.map((stat, index) => (
            <div key={index} className="text-center">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-blue-100 text-blue-600 rounded-full mb-4">
                {stat.icon}
              </div>
              <div className="text-3xl font-bold text-gray-900 mb-2">{stat.value}</div>
              <div className="text-lg font-semibold text-gray-700 mb-1">{stat.label}</div>
              <div className="text-sm text-gray-600">{stat.description}</div>
            </div>
          ))}
        </div>

        {/* Featured Testimonial */}
        {testimonials.filter(t => t.featured).map((testimonial) => (
          <div key={testimonial.id} className="bg-gradient-to-r from-blue-50 to-purple-50 rounded-2xl p-8 mb-16">
            <div className="max-w-4xl mx-auto text-center">
              <Quote className="w-12 h-12 text-blue-600 mx-auto mb-6" />
              <blockquote className="text-2xl sm:text-3xl font-medium text-gray-900 mb-8 leading-relaxed">
                "{testimonial.content}"
              </blockquote>
              <div className="flex items-center justify-center mb-6">
                {renderStars(testimonial.rating)}
              </div>
              <div className="flex items-center justify-center space-x-4">
                <img
                  src={testimonial.image}
                  alt={testimonial.name}
                  className="w-16 h-16 rounded-full bg-gray-200"
                />
                <div className="text-left">
                  <div className="font-semibold text-gray-900">{testimonial.name}</div>
                  <div className="text-gray-600">{testimonial.role}</div>
                  <div className="text-sm text-gray-500">{testimonial.company}</div>
                </div>
              </div>
              
              {testimonial.metrics && (
                <div className="grid grid-cols-2 gap-8 mt-8 pt-8 border-t border-gray-200">
                  {testimonial.metrics.timeSaved && (
                    <div className="text-center">
                      <div className="text-2xl font-bold text-green-600">{testimonial.metrics.timeSaved}</div>
                      <div className="text-gray-600">Time Saved Daily</div>
                    </div>
                  )}
                  {testimonial.metrics.revenueIncrease && (
                    <div className="text-center">
                      <div className="text-2xl font-bold text-blue-600">{testimonial.metrics.revenueIncrease}</div>
                      <div className="text-gray-600">Revenue Increase</div>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        ))}

        {/* Testimonials Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8">
          {testimonials.filter(t => !t.featured).slice(0, testimonialsVariant === 'A' ? 3 : 6).map((testimonial, index) => (
            <div 
              key={testimonial.id} 
              className="bg-white rounded-xl shadow-lg border border-gray-200 p-6 hover:shadow-xl transition-all duration-300 transform hover:scale-105"
              style={{ animationDelay: `${index * 150}ms` }}
            >
              {/* Rating */}
              <div className="flex items-center mb-4">
                {renderStars(testimonial.rating)}
              </div>

              {/* Content */}
              <blockquote className="text-gray-700 mb-6 line-clamp-4">
                "{testimonial.content}"
              </blockquote>

              {/* Metrics */}
              {testimonial.metrics && (
                <div className="grid grid-cols-2 gap-4 mb-6 pt-4 border-t border-gray-100">
                  {testimonial.metrics.timeSaved && (
                    <div className="text-center">
                      <div className="text-lg font-bold text-green-600">{testimonial.metrics.timeSaved}</div>
                      <div className="text-xs text-gray-600">Saved</div>
                    </div>
                  )}
                  {testimonial.metrics.revenueIncrease && (
                    <div className="text-center">
                      <div className="text-lg font-bold text-blue-600">{testimonial.metrics.revenueIncrease}</div>
                      <div className="text-xs text-gray-600">Revenue</div>
                    </div>
                  )}
                  {testimonial.metrics.efficiency && (
                    <div className="text-center col-span-2">
                      <div className="text-lg font-bold text-purple-600">{testimonial.metrics.efficiency}</div>
                      <div className="text-xs text-gray-600">Efficiency</div>
                    </div>
                  )}
                </div>
              )}

              {/* Author */}
              <div className="flex items-center">
                <img
                  src={testimonial.image}
                  alt={testimonial.name}
                  className="w-12 h-12 rounded-full bg-gray-200 mr-4"
                />
                <div>
                  <div className="font-semibold text-gray-900">{testimonial.name}</div>
                  <div className="text-sm text-gray-600">{testimonial.role}</div>
                  <div className="text-xs text-gray-500">{testimonial.company}</div>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Trust Badges */}
        <div className="mt-16 pt-12 border-t border-gray-200">
          <div className="text-center mb-8">
            <h3 className="text-2xl font-bold text-gray-900 mb-4">Trusted by Industry Leaders</h3>
            <p className="text-gray-600">Used by photographers contributing to major platforms</p>
          </div>
          
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-8 items-center justify-items-center opacity-70">
            <img
              src="/images/logos/shutterstock-logo.png"
              alt="Shutterstock"
              className="h-6 sm:h-8 w-auto grayscale hover:grayscale-0 transition-all duration-300"
            />
            <img
              src="/images/logos/getty-images-logo.png"
              alt="Getty Images"
              className="h-6 sm:h-8 w-auto grayscale hover:grayscale-0 transition-all duration-300"
            />
            <img
              src="/images/logos/adobe-stock-logo.png"
              alt="Adobe Stock"
              className="h-6 sm:h-8 w-auto grayscale hover:grayscale-0 transition-all duration-300"
            />
            <img
              src="/images/logos/unsplash-logo.png"
              alt="Unsplash"
              className="h-6 sm:h-8 w-auto grayscale hover:grayscale-0 transition-all duration-300"
            />
            <img
              src="/images/logos/istock-logo.png"
              alt="iStock"
              className="h-6 sm:h-8 w-auto grayscale hover:grayscale-0 transition-all duration-300"
            />
          </div>
        </div>

        {/* CTA Section */}
        <div className="mt-16 text-center">
          <div className="bg-gradient-to-r from-blue-600 to-purple-600 rounded-2xl p-8 text-white">
            <h3 className="text-3xl font-bold mb-4">Ready to Transform Your Workflow?</h3>
            <p className="text-xl text-blue-100 mb-8 max-w-2xl mx-auto">
              Join thousands of photographers who are saving hours daily and increasing their revenue.
            </p>
            <button className="bg-white text-blue-600 font-bold py-4 px-8 rounded-lg text-lg hover:bg-gray-100 transition-colors shadow-lg">
              Start Your Free Trial Today
            </button>
            <div className="text-blue-100 text-sm mt-4">
              100 free credits • No credit card required • Setup in 2 minutes
            </div>
          </div>
        </div>
      </div>
    </section>
  )
}